import { Stepper, Step, StepLabel, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import type { PaymentMethodCreditCardSteps_charge$key } from './__generated__/PaymentMethodCreditCardSteps_charge.graphql.ts';
import PaymentMethodCreditCardValue from './PaymentMethodCreditCardValue.tsx';
import PaymentMethodCreditCardAddBillingAddressStep from './steps/PaymentMethodCreditCardBillingAddressStep.tsx';
import PaymentMethodCreditCardConfirmStep from './steps/PaymentMethodCreditCardConfirmStep.tsx';
import PaymentMethodCreditCardCustomerData from './steps/PaymentMethodCreditCardCustomerData.tsx';
import PaymentMethodCreditCardCustomerRegisterStep from './steps/PaymentMethodCreditCardCustomerRegisterStep.tsx';
import PaymentLinkAdditionalInfo from '../../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../../paymentLink/PaymentLinkComment.tsx';
import PaymentMethodPaymentDeadline from '../../paymentMethod/PaymentMethodPaymentDeadline.tsx';
import PaymentMethodTransactionDetails from '../../paymentMethod/PaymentMethodTransactionDetails.tsx';

type PaymentMethodCreditCardSelectedProps = {
  charge: PaymentMethodCreditCardSteps_charge$key;
};

const PaymentMethodCreditCardSteps = ({
  charge: chargeKey,
}: PaymentMethodCreditCardSelectedProps): ReactNode => {
  const [billingAddress, setBillingAddress] = useState(null);

  const { t } = useTranslation();

  const theme = useTheme();

  const steps = [t('Identification'), t('Address'), t('Card details')];

  const charge = useFragment(
    graphql`
      fragment PaymentMethodCreditCardSteps_charge on Charge {
        id
        status
        customer {
          id
          name
          taxID {
            taxID
          }
          email
          phone
        }
        ...PaymentMethodCreditCardValue_charge
        ...PaymentMethodTransactionDetails_charge
        ...PaymentLinkAdditionalInfo_charge
        ...PaymentMethodPaymentDeadline_charge
        ...PaymentMethodCreditCardCustomerRegisterStep_charge
        ...PaymentMethodCreditCardConfirmStep_charge
        ...PaymentLinkComment_charge
      }
    `,
    chargeKey,
  );

  const activeStep = useMemo(() => {
    if (!charge?.customer?.id) {
      return 0;
    }

    if (!billingAddress) {
      return 1;
    }

    return 2;
  }, [charge?.customer?.id, billingAddress]);

  const stepContent = [
    <PaymentMethodCreditCardCustomerRegisterStep charge={charge} />,
    <PaymentMethodCreditCardAddBillingAddressStep
      setBillingAddress={setBillingAddress}
    />,
    <PaymentMethodCreditCardConfirmStep
      charge={charge}
      billingAddress={billingAddress}
    />,
  ];

  return (
    <Stack sx={{ width: '100%' }} spacing={4}>
      <PaymentMethodCreditCardValue charge={charge} />
      <PaymentMethodCreditCardCustomerData customer={charge?.customer} />
      <PaymentLinkComment charge={charge} />
      <Stepper
        activeStep={activeStep}
        alternativeLabel
        sx={{
          width: '100%',
          borderTop: `thin solid ${theme.palette.grey[300]}`,
          paddingTop: 4,
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Stack>{stepContent[activeStep]}</Stack>

      <Stack sx={{ gap: 1 }}>
        <PaymentMethodPaymentDeadline charge={charge} />
        <PaymentMethodTransactionDetails charge={charge} />
        <PaymentLinkAdditionalInfo charge={charge} />
      </Stack>
    </Stack>
  );
};

export default PaymentMethodCreditCardSteps;
